import React, { useCallback, useContext, useMemo } from 'react'
import { FlightDropdownOption } from 'components/Flights/types'
import { fareTypes, fareCabins, FlightsFareTypes } from 'constants/flight'
import { FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'

import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import FareDropdownMobile from './FareDropdown/FareDropdownMobile'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FlightSearchTravellerSelectField from './FlightTravellerSelect/FlightSearchTravellerSelectField'
import Group from 'components/utils/Group'
import BusinessTravellerAccountGuard from 'businessTraveller/components/BusinessTravellerAccountGuard'
import BusinessTravellerSelectFlightsMobile from 'businessTraveller/components/select-traveller/BusinessTravellerSelectFlightsMobile'
import config from 'constants/config'
import BusinessTravellerSelectLoadingSkeleton from 'businessTraveller/components/select-traveller/BusinessTravellerSelectLoadingSkeleton'
import FlightSearchAirportModal from './FlightAirportSelect/FlightSearchAirportModal'
import ModalContext from 'contexts/ModalContext'
import SearchFormField from 'components/SearchV2/Components/SearchFormField/SearchFormField'
import FlightSearchMenuMobile from './FlightSearchMenu/FlightSearchMenuMobile'
import FlightsDatePicker from './FlightsDatePicker/FlightsDatePicker'
import { addDays } from '@luxuryescapes/lib-refunds/build/lib/date'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import SearchFormFieldGroup from 'components/SearchV2/Components/SearchFormField/SearchFormFieldGroup'
import FlightSearchTravellerSelectContent from './FlightTravellerSelect/FlightSearchTravellerSelectContent'
import { getAirportDisplayText } from 'lib/flights/flightUtils'
import useFlightDeal from 'hooks/Flights/useFlightDeal'
import FlightDealDuration from 'components/Flights/FlightMerchandising/FlightDealDuration/FlightDealDuration'
import BodyText from 'components/Luxkit/Typography/BodyText'

interface Props {
  /**
   * Sets the airports to a 'read only' mode
   * Used when defaulting airport selections such as on flight deal pages
   */
  readOnlyAirports?: boolean;
}

function FlightSearchWidgetMobile(props: Props) {
  const { readOnlyAirports } = props

  const state = useContext(FlightSearchWidgetStateContext)
  const dispatch = useContext(FlightSearchWidgetDispatchContext)

  const isBusinessTravellerSelectEnabled = config.businessTraveller.selectTravellerEnabled

  const {
    fareType,
    fareCabin,
    departureAirport,
    arrivalAirport,
    checkinDate,
    checkoutDate,
    flightDealId,
  } = state

  const [flightDeal] = useFlightDeal(flightDealId)

  const changeFareType = useCallback((fareType: FlightDropdownOption) => {
    dispatch({ type: FlightSearchWidgetActions.SET_FARE_TYPE, fareType })
  }, [dispatch])

  const changeFareCabin = useCallback((fareCabin: FlightDropdownOption) => {
    dispatch({ type: FlightSearchWidgetActions.SET_FARE_CABIN, fareCabin })
  }, [dispatch])

  const showModal = useContext(ModalContext)

  const onTravellerSelect = useCallback(async() => {
    await showModal<any>(<FlightSearchMenuMobile
      title="Travellers"
      showTravellers
    >
      <FlightSearchTravellerSelectContent />
    </FlightSearchMenuMobile>)
  }, [showModal])

  const earliestBookingDate = useMemo(() => addDays(new Date(), 1), [])
  const finalBookingDate = useMemo(() => addDays(new Date(), 330), [])

  const onDateSelect = useCallback(async() => {
    const dates = await showModal<any>(<FlightSearchMenuMobile
      title="When do you want to go?"
      showDates
    >
      <Group direction="vertical" verticalAlign="space-between" gap={12} fullHeight>
        <FlightsDatePicker
          minDate={earliestBookingDate}
          maxDate={finalBookingDate}
          type="single"
        />
        <VerticalSpacer gap={12}>
          {flightDeal && <FlightDealDuration flightDeal={flightDeal} />}
          <BodyText variant="small" colour="neutral-two">
            Based on recent return prices (per adult) found
          </BodyText>
        </VerticalSpacer>
      </Group>
    </FlightSearchMenuMobile>,
    )

    // disable traveller selection for LEBT
    if (!isBusinessTravellerSelectEnabled && dates) {
      onTravellerSelect()
    }
  }, [earliestBookingDate, finalBookingDate, flightDeal, onTravellerSelect, showModal, isBusinessTravellerSelectEnabled])

  const onArrivalSelect = useCallback(async() => {
    const airport = await showModal<App.AirportLocation | undefined>(
      <FlightSearchAirportModal direction="arrival" />,
    )
    if (airport) {
      onDateSelect()
    }
  }, [onDateSelect, showModal])

  const onDepartureSelect = useCallback(async() => {
    const airport = await showModal<App.AirportLocation | undefined>(
      <FlightSearchAirportModal direction="departure" />,
    )
    if (airport) {
      onArrivalSelect()
    }
  }, [onArrivalSelect, showModal])

  return (
    <VerticalSpacer gap={12}>
      <Group direction="horizontal" gap={8}>
        <FareDropdownMobile
          options={fareTypes}
          title="Flight type"
          selectedOption={fareType}
          onOptionChange={changeFareType}
        />
        <FareDropdownMobile
          options={fareCabins}
          title="Class"
          selectedOption={fareCabin}
          onOptionChange={changeFareCabin}
        />
      </Group>
      <SearchFormField
        value={getAirportDisplayText(departureAirport)}
        label="From"
        required
        placeholder="Search by city or airport"
        onClick={readOnlyAirports ? undefined : onDepartureSelect}
        requiredErrorMessage="Please enter a departure"
      />
      <SearchFormField
        value={getAirportDisplayText(arrivalAirport)}
        label="To"
        required
        placeholder="Search by city or airport"
        onClick={readOnlyAirports ? undefined : onArrivalSelect}
        requiredErrorMessage="Please enter a destination"
      />
      <SearchFormFieldGroup>
        <SearchFormField
          label="Departing"
          placeholder="Select date"
          required
          requiredErrorMessage="Please select travel dates"
          value={checkinDate?.format(DMY_CASUAL_FORMAT)}
          onClick={onDateSelect}
        />
        {fareType.value === FlightsFareTypes.RETURN && <SearchFormField
          label="Returning"
          placeholder="Select date"
          required
          requiredErrorMessage="Please select travel dates"
          value={checkoutDate?.format(DMY_CASUAL_FORMAT)}
          onClick={onDateSelect}
        />}
      </SearchFormFieldGroup>
      {isBusinessTravellerSelectEnabled && <BusinessTravellerAccountGuard
        accountMode="business"
        employeeRole="BUSINESS_ADMIN"
        loading={<BusinessTravellerSelectLoadingSkeleton />}
        fallback={<FlightSearchTravellerSelectField onClick={onTravellerSelect} disabled />}
      >
        <BusinessTravellerSelectFlightsMobile />
      </BusinessTravellerAccountGuard>}
      {!isBusinessTravellerSelectEnabled && <FlightSearchTravellerSelectField onClick={onTravellerSelect} />}
    </VerticalSpacer>
  )
}

export default FlightSearchWidgetMobile
